// PanelMenu.tsx — ONE control where the fleet has two circles.
//
// 🔴 IDENTITY (2026-08-30): "wordmark-left + two-circular-controls-right" is one of the four
// banned fleet fingerprints. Garage lost the wordmark and moved the circles off the odometer,
// but two same-size circles in the same corner were still the last element every sibling shared.
// This collapses account + backup behind a SINGLE square instrument control, in Garage's own
// language (square, hairline, lit) rather than a third circle.
//
// The shared AccountMenu / BackupMenu components are used UNCHANGED — the account-deletion flow
// the store gate depends on is inside AccountMenu and must not be reimplemented here.

import { useState, type ReactNode } from "react";
import { platform } from "./tauri-web";
import { WalletTopUp } from "./iap";

export function PanelMenu({ backup, account }: { backup: ReactNode; account: ReactNode }) {
  const [open, setOpen] = useState(false);
  // Apple 2.1(b) (Focus/Gifted rejections, 2026-09-05): the wallet top-up must be visible
  // the moment this drawer opens, not nested one tap deeper inside AccountMenu's own avatar
  // popover. Rendered directly here, gated iOS/MAS same as AccountMenu's own gate.
  const isIos = platform() === "ios";
  const isMasMac = platform() === "macos" && (import.meta as any).env?.VITE_MAS === "1";
  return (
    <div style={{ position: "relative" }}>
      <button
        aria-label="Settings, backup and account"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{
          width: 34, height: 34, borderRadius: 0, cursor: "pointer",
          background: "linear-gradient(180deg, var(--elev-2), var(--elev-1))",
          border: "1px solid var(--border)", color: "var(--ink-2)",
          display: "grid", placeItems: "center",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
        }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="3.1" stroke="currentColor" strokeWidth="1.7" />
          <path d="M12 3.6v2.3M12 18.1v2.3M3.6 12h2.3M18.1 12h2.3M6.1 6.1l1.6 1.6M16.3 16.3l1.6 1.6M17.9 6.1l-1.6 1.6M7.7 16.3l-1.6 1.6"
                stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div
            style={{
              position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 50, minWidth: 210,
              background: "var(--elev-1)", border: "1px solid var(--border)", borderRadius: 0,
              boxShadow: "var(--shadow-2)", padding: "0.25rem",
            }}
          >
            {(isIos || isMasMac) && (
              <>
                <div style={{ padding: "0.5rem 0.5rem 0.15rem" }}><WalletTopUp /></div>
                <div style={{ height: 1, background: "var(--border-subtle)" }} />
              </>
            )}
            {/* 🔴 className is LOAD-BEARING — see `.odo-menu-row > div` in styles.css. The shared
                BackupMenu / AccountMenu each wrap themselves in an INLINE position:relative div, so
                their `right: 0` popover anchors to a 34px icon button near this panel's LEFT edge
                and lands off-screen. The stylesheet re-anchors it to the panel. */}
            <div className="odo-menu-row" style={{ display: "flex", alignItems: "center", gap: "0.6rem", padding: "0.45rem 0.5rem" }}>
              {backup}
              <span style={{ fontSize: "var(--fs-sm)", color: "var(--ink-1)" }}>Back up &amp; restore</span>
            </div>
            <div style={{ height: 1, background: "var(--border-subtle)" }} />
            <div className="odo-menu-row" style={{ display: "flex", alignItems: "center", gap: "0.6rem", padding: "0.45rem 0.5rem" }}>
              {account}
              <span style={{ fontSize: "var(--fs-sm)", color: "var(--ink-1)" }}>Account</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
